import { useState } from "react";
import { useLessonsAdded } from "../../../hooks/useDashboardQueries";
import {
	LessonCounts,
	LessonMonths,
	LessonsAddedResponse,
} from "../../../types/dashboard.types";
import { cardStatus } from "../cardStatus";
import TrendChart, { TrendDatum } from "../charts/TrendChart";
import DashboardCard from "../DashboardCard";
import {
	BADGES,
	formatCount,
	formatMonthKey,
	LESSON_MONTH_OPTIONS,
	LESSON_SERIES,
} from "../dashboardConfig";
import ExpandedWidgetModal from "../ExpandedWidgetModal";
import {
	LookupField,
	ProjectField,
	SelectField,
	TenantField,
} from "../FilterControls";
import {
	masterTenantName,
	TenantChoice,
	TenantWidgetProps,
} from "../tenantChoice";
import {
	useDraftFilters,
	useExpandedView,
	useHasOpened,
	useTenantChoice,
} from "../widgetHooks";
import WidgetSelect from "../WidgetSelect";
import WidgetState from "../WidgetState";

const TITLE = "Lessons";
const EXPANDED_TITLE = "Lessons added";
const EMPTY_TEXT = "No lessons in this period";

const monthsLabel = (months: LessonMonths) =>
	LESSON_MONTH_OPTIONS.find((option) => option.value === months)?.label ??
	`Past ${months} months`;

const hasNoLessons = (data: LessonsAddedResponse) =>
	data.months.every((month) => !month.internal && !month.external);

interface LessonsAddedChartProps {
	data: LessonsAddedResponse;
	ownLabel: string;
	context: string;
}

function LessonsAddedChart({
	data,
	ownLabel,
	context,
}: LessonsAddedChartProps) {
	const points: TrendDatum[] = data.months.map((month) => ({
		id: month.month,
		title: formatMonthKey(month.month, "MMM YYYY"),
		internal: month.internal,
		external: month.external,
	}));
	const totals = data.months.reduce(
		(sum, month) => ({
			internal: sum.internal + month.internal,
			external: sum.external + month.external,
		}),
		{ internal: 0, external: 0 }
	);

	return (
		<TrendChart
			data={points}
			series={LESSON_SERIES}
			// "Jan 25" on January and the first point, "Feb" elsewhere.
			tickLabel={(point, index) =>
				formatMonthKey(
					point.id,
					index === 0 || point.id.endsWith("-01") ? "MMM YY" : "MMM"
				)
			}
			valueDetail={
				data.months.some((month) => month.own)
					? (point, seriesKey) => {
							const own = data.months.find((month) => month.month === point.id)
								?.own?.[seriesKey as keyof LessonCounts];
							return own === undefined
								? undefined
								: `(${ownLabel} ${formatCount(own)})`;
						}
					: undefined
			}
			yAxisSide="right"
			legendSide="top"
			variant="gradient"
			summary={`${EXPANDED_TITLE}, ${context}: ${totals.internal} internal and ${totals.external} external.`}
		/>
	);
}

interface ExpandedProps {
	open: boolean;
	onClose: () => void;
	choice: TenantChoice;
	initialTenant: string;
	initialMonths: LessonMonths;
	ownLabel: string;
}

function LessonsAddedExpanded({
	open,
	onClose,
	choice,
	initialTenant,
	initialMonths,
	ownLabel,
}: ExpandedProps) {
	const hasOpened = useHasOpened(open);
	const filters = useDraftFilters<{
		tenant: string;
		months: LessonMonths;
		project?: string;
		discipline?: string;
		lessonType?: string;
	}>({ tenant: initialTenant, months: initialMonths });
	const { applied, draft } = filters;
	const { target, label } = choice.resolve(applied.tenant);
	const draftTarget = choice.resolve(draft.tenant).target;
	const query = useLessonsAdded(hasOpened ? target : null, applied.months, {
		project: target?.scope === "tenant" ? applied.project : undefined,
		discipline: applied.discipline,
		lessonType: applied.lessonType,
	});
	const context = `${label} · ${monthsLabel(applied.months)}`;

	return (
		<ExpandedWidgetModal
			open={open}
			onClose={onClose}
			badge={BADGES.lessons}
			title={EXPANDED_TITLE}
			context={context}
			onApply={filters.apply}
			onReset={filters.reset}
			filters={
				<>
					<TenantField
						value={draft.tenant}
						options={choice.options}
						onChange={(tenant) =>
							filters.update({ tenant, project: undefined })
						}
					/>
					<SelectField
						label="Period"
						value={draft.months}
						options={LESSON_MONTH_OPTIONS}
						onChange={(months) => filters.update({ months })}
					/>
					{draftTarget?.scope === "tenant" && (
						<ProjectField
							tenantKey={draftTarget.tenantKey}
							value={draft.project}
							onChange={(project) => filters.update({ project })}
						/>
					)}
					<LookupField
						label="Discipline"
						lookup="discipline"
						value={draft.discipline}
						onChange={(discipline) => filters.update({ discipline })}
					/>
					<LookupField
						label="Lesson type"
						lookup="lesson_learned_type"
						value={draft.lessonType}
						onChange={(lessonType) => filters.update({ lessonType })}
					/>
				</>
			}
		>
			<WidgetState
				status={cardStatus(query, hasNoLessons)}
				emptyText={EMPTY_TEXT}
				onRetry={query.refetch}
				truncated={query.data?.truncated}
			>
				{query.data && (
					<LessonsAddedChart
						data={query.data}
						ownLabel={ownLabel}
						context={context}
					/>
				)}
			</WidgetState>
		</ExpandedWidgetModal>
	);
}

/** Lessons created per month, internal against external. */
function LessonsAddedWidget({ tenants, currentTenantKey }: TenantWidgetProps) {
	const { choice, tenant, setTenant, target, label } = useTenantChoice(
		tenants,
		"master",
		currentTenantKey
	);
	const [months, setMonths] = useState<LessonMonths>(12);
	const expanded = useExpandedView();
	const query = useLessonsAdded(target, months);
	const ownLabel = masterTenantName(tenants);

	return (
		<>
			<DashboardCard
				badge={BADGES.lessons}
				title={TITLE}
				className="dashboard-widget--lessons-added dashboard-widget--short"
				controls={
					<>
						<WidgetSelect
							value={tenant}
							options={choice.options}
							onChange={setTenant}
							ariaLabel={`Tenant for ${TITLE}`}
						/>
						<WidgetSelect
							value={months}
							options={LESSON_MONTH_OPTIONS}
							onChange={setMonths}
							ariaLabel={`Period for ${TITLE}`}
						/>
					</>
				}
				onExpand={expanded.show}
				status={cardStatus(query, hasNoLessons)}
				emptyText={EMPTY_TEXT}
				forbiddenText={`You don't have access to Lessons learned for ${label}.`}
				onRetry={query.refetch}
				truncated={query.data?.truncated}
			>
				{query.data && (
					<LessonsAddedChart
						data={query.data}
						ownLabel={ownLabel}
						context={`${label} · ${monthsLabel(months)}`}
					/>
				)}
			</DashboardCard>
			<LessonsAddedExpanded
				key={expanded.session}
				open={expanded.open}
				onClose={expanded.hide}
				choice={choice}
				initialTenant={tenant}
				initialMonths={months}
				ownLabel={ownLabel}
			/>
		</>
	);
}

export default LessonsAddedWidget;
